import {
  type ArcualUserFlags,
  type ArcualUserProfile,
  type CreateUserProfilePayload,
  type CreateUserProfilePayloadAuth0,
  type CreateUserProfileResponse,
  getUserService,
} from '@art-basel/artbasel-lib-platform';
import { cdn } from '@/utils/cdn';
import { handleHttpError } from '@/utils/errorHandlers';
import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { type Ref, ref } from 'vue';
import { env } from '@/constants';
import { setUsetifulTags } from 'usetiful-sdk';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';

const { featureFlags } = useAmplitudeFeatureFlags();

export const UserStoreName = 'UserStore';

export const useUserStore = defineStore(UserStoreName, () => {
  const user = ref<ArcualUserProfile | undefined>();

  const users = ref<Record<string, ArcualUserProfile>>({});

  const setUser = (userProfile?: ArcualUserProfile) => {
    user.value = userProfile;

    if (env.VITE_FEATURE_SENTRY_ENABLED) {
      // inject id/email into support requests for logged-in users
      Sentry.setTag('user.id', userProfile?.id);
      Sentry.setTag('user.email', userProfile?.email);
    }

    if (env.VITE_USETIFUL_ENABLED) {
      setUsetifulTags({
        userId: userProfile?.id,
        email: userProfile?.email,
        name: userProfile?.name,
      });
    }

    if (userProfile?.id) {
      users.value[userProfile.id] = userProfile;
    }
  };

  const loadUser = async () => {
    try {
      let result: ArcualUserProfile;
      if (featureFlags.value.isAuth0MigrationEnabled) {
        const currentUser = await getUserService().getCurrentUser();
        const platformAccountId = currentUser?.platformAccount?.platformAccountId;
        if (!platformAccountId) {
          throw new Error('No platform account id for user');
        }

        // TODO: use the currentUser.platformAccount when BE returns proper schema
        // remove this mapping when possible
        result = {
          id: platformAccountId,
          name: currentUser.platformAccount.businessName,
          email: currentUser.platformAccount.email || '',
          phoneNumber: '',
          imageUrl: currentUser.platformUser?.imageUrl,
          privacyPolicyAccepted: true,
          termsConditionsAccepted: currentUser.consents?.termsAndConditions || false,
        } as ArcualUserProfile;

      } else {
        result = await getUserService().getUserProfile();
      }

      result.flags = await getUserFlags();

      setUser(result);
    } catch (e) {
      handleHttpError(e);
    }
  };

  const clearCurrentUser = () => setUser(undefined);

  const currentUser = (): Ref<ArcualUserProfile | undefined> => user;

  const getProfileById = async (id: string) => {
    let result = users.value[id];
    if (result) {
      return result;
    }

    try {
      result = await getUserService().getUserProfileById(id);

      users.value[result.id] = result;

      return result;
    } catch (e) {
      handleHttpError(e);
      delete users.value[result.id];

      return null as unknown as ArcualUserProfile;
    }
  };

  const getUserFlags = async () => {
    try {
      return await getUserService().getUserFlags();
    } catch (e) {
      handleHttpError(e);
      return {} as unknown as ArcualUserFlags;
    }
  };

  const updateUserFlags = async (flags: ArcualUserFlags) => {
    try {
      const response = await getUserService().updateUserFlags(flags);

      if (user.value) user.value.flags = flags;
      return response;
    } catch (e) {
      handleHttpError(e);
      return Promise.reject(e);
    }
  };

  const updateUserProfile = async (userProfile: ArcualUserProfile) => {
    try {
      // Exclude imageUrl because it is already handled in the uploadProfilePicture
      const profileToUpdate = { ...userProfile };
      delete profileToUpdate.imageUrl;

      const result = await getUserService().updateUserProfile(profileToUpdate);
      setUser(result);
      return result;
    } catch (e) {
      handleHttpError(e);
      return Promise.reject(e);
    }
  };

  const updateProfilePicture = async (picture: File) => {
    if (user.value) {
      await getUserService()
        .uploadProfilePicture(user.value?.id, picture)
        .then((response) => {
          if (user.value) {
            user.value.imageUrl = cdn(response);

            if (users.value[user.value.id]) {
              users.value[user.value.id].imageUrl = cdn(response);
            }
          }
        })
        .catch(handleHttpError);
    }
  };

  const createUserProfile = async (galleryUserProfile: CreateUserProfilePayload): Promise<CreateUserProfileResponse> => {
    try {
      return await getUserService().createUserProfile(galleryUserProfile);
    } catch (e) {
      handleHttpError(e);
      throw e;
    }
  };

  const createUserProfileAuth0 = async (galleryUserProfile: CreateUserProfilePayloadAuth0): Promise<CreateUserProfileResponse> => {
    try {
      return await getUserService().createUserProfileAuth0(galleryUserProfile);
    } catch (e) {
      handleHttpError(e);
      throw e;
    }
  };

  const reset = () => {
    setUser(undefined);
    users.value = {};
  };

  return {
    currentUser,
    clearCurrentUser,
    loadUser,
    updateUserProfile,
    createUserProfile,
    createUserProfileAuth0,
    getProfileById,
    updateProfilePicture,
    reset,
    updateUserFlags,
  };
});
